/*
 * Akyta Chat — widget (JS vanilla, fără dependențe).
 * Sursa e în repo-ul akyta-chat (widget/); în temă ajunge cu scripts/sync-widget.ps1.
 * Nu edita copia din tema Shopify.
 *
 * Config: <script type="application/json" id="akyta-chat-config">, randat de snippets/akyta-chat.liquid.
 * Istoricul din sessionStorage e doar pentru afișare (navigare între pagini în același tab);
 * istoricul pe care îl vede modelul e cel din baza de date, pe server.
 *
 * API: window.AkytaChat.open(), .close(), .openTicket({ category, summary, intro })
 * — openTicket deschide panoul direct pe formularul de tichet (ex. din pagina de tracking),
 * fără conversație înainte; sesiunea o creează serverul la trimitere.
 */
(() => {
  'use strict';

  const configEl = document.getElementById('akyta-chat-config');
  if (!configEl || window.AkytaChat) return;

  let cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (_) {
    return;
  }
  if (!cfg || !cfg.endpoint) return;

  const S = cfg.strings || {};
  const ENDPOINT = String(cfg.endpoint).replace(/\/+$/, '');
  const STORE_KEY = 'akyta-chat-v1';
  const MAX_STORED = 50;
  const MAX_CHARS = 1000;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const ALLOWED_HOSTS = new Set(['akyta.ro', 'www.akyta.ro', location.host]);
  const MOBILE = window.matchMedia('(max-width: 640px)');

  // ---------------------------------------------------------------- stare

  const state = restore();
  let busy = false;
  let ui = null; // construit la prima deschidere

  function restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      if (saved && Array.isArray(saved.messages)) {
        return { sessionId: saved.sessionId, messages: saved.messages, open: Boolean(saved.open) };
      }
    } catch (_) {
      // sessionStorage blocat (mod privat, setări): mergem fără istoric
    }
    return { sessionId: undefined, messages: [], open: false };
  }

  function persist() {
    try {
      sessionStorage.setItem(
        STORE_KEY,
        JSON.stringify({ sessionId: state.sessionId, open: state.open, messages: state.messages.slice(-MAX_STORED) }),
      );
    } catch (_) {
      // idem
    }
  }

  // ---------------------------------------------------------------- DOM

  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs || {})) {
      if (value === undefined || value === null || value === false) continue;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
      else node.setAttribute(key, value === true ? '' : String(value));
    }
    for (const child of children.flat()) if (child !== null && child !== undefined) node.append(child);
    return node;
  }

  // SVG-uri statice (nu conțin date de la utilizator).
  const ICONS = {
    chat: '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 3C6.5 3 2 6.6 2 11c0 2.2 1.1 4.2 2.9 5.6-.2 1.5-.9 2.9-2 3.9 2.1 0 4-.7 5.4-1.9 1.2.3 2.4.5 3.7.5 5.5 0 10-3.6 10-8S17.5 3 12 3z"/></svg>',
    close: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/></svg>',
    reset: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4"/></svg>',
    send: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12 2-12 2z"/></svg>',
  };

  function icon(name) {
    const tpl = document.createElement('template');
    tpl.innerHTML = ICONS[name];
    return tpl.content.firstChild;
  }

  // ---------------------------------------------------------------- text (markdown minim, sigur)

  /** Doar linkuri către magazin; restul devin text. */
  function safeUrl(href) {
    try {
      const url = new URL(href, location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return ALLOWED_HOSTS.has(url.host) ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  /** **bold**, [text](url) și URL-uri simple; tot restul e text (fără innerHTML). */
  function inline(text) {
    const out = [];
    const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\((\S+?)\)|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?])/g;
    let last = 0;
    let m;
    while ((m = re.exec(text))) {
      if (m.index > last) out.push(text.slice(last, m.index));
      if (m[1] !== undefined) {
        out.push(el('strong', null, inline(m[1])));
      } else {
        const raw = m[3] !== undefined ? m[3] : m[4];
        const href = safeUrl(raw);
        const label = m[2] !== undefined ? m[2] : raw.replace(/^https?:\/\/(www\.)?/, '');
        out.push(href ? el('a', { href, class: 'akc-link' }, label) : m[0]);
      }
      last = re.lastIndex;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  }

  function markdown(text) {
    const frag = document.createDocumentFragment();
    let list = null;
    for (const raw of String(text).split('\n')) {
      const line = raw.trim();
      const item = /^(?:[-•*]|(\d+)[.)])\s+(.*)$/.exec(line);
      if (item) {
        if (!list) frag.append((list = el(item[1] ? 'ol' : 'ul', { class: 'akc-list' })));
        list.append(el('li', null, inline(item[2])));
        continue;
      }
      list = null;
      if (line) frag.append(el('p', null, inline(line)));
    }
    return frag;
  }

  // ---------------------------------------------------------------- mesaje

  function productCard(p) {
    const href = safeUrl(p.url);
    return el(
      href ? 'a' : 'div',
      { class: 'akc-card', href: href || undefined },
      p.image ? el('img', { src: p.image, alt: '', loading: 'lazy', width: 64, height: 64 }) : el('span', { class: 'akc-card__ph' }),
      el(
        'span',
        { class: 'akc-card__body' },
        el('span', { class: 'akc-card__title', text: p.title }),
        el(
          'span',
          { class: 'akc-card__price' },
          p.compare_at ? el('s', { text: p.compare_at }) : null,
          el('b', { text: p.price }),
        ),
        el('span', {
          class: `akc-card__stock ${p.available ? 'is-in' : 'is-out'}`,
          text: p.available ? S.available : S.unavailable,
        }),
      ),
    );
  }

  let fieldCount = 0;
  const nextId = () => `akc-f${++fieldCount}`;

  function field(label, control) {
    control.id = nextId();
    return el('div', { class: 'akc-field' }, el('label', { class: 'akc-field__label', for: control.id, text: label }), control);
  }

  function ticketBlock(msg) {
    const t = msg.ticket;
    if (t.done) {
      // t.done = textul de confirmare (conține emailul clientului; rămâne doar în sessionStorage, în tab).
      return el('p', { class: 'akc-ticket-done', role: 'status', text: t.done });
    }

    // „contact” = clientul vrea să fie sunat: telefonul e obligatoriu.
    const needsPhone = t.category === 'contact';
    const name = el('input', { type: 'text', name: 'name', autocomplete: 'name', maxlength: 100 });
    const email = el('input', { type: 'email', name: 'email', autocomplete: 'email', maxlength: 254, required: true });
    const phone = el('input', { type: 'tel', name: 'phone', autocomplete: 'tel', maxlength: 20, required: needsPhone });
    const order = el('input', { type: 'text', name: 'order', maxlength: 31 });
    const summary = el('textarea', { name: 'summary', rows: 3, maxlength: 2000, required: true });
    summary.value = t.summary || '';
    const consent = el('input', { type: 'checkbox', name: 'consent', id: nextId() });
    const error = el('p', { class: 'akc-form-error', role: 'alert', hidden: true });
    const submit = el('button', { type: 'submit', class: 'akc-btn', text: S.ticket_submit });

    const fail = (text) => {
      error.textContent = text;
      error.hidden = false;
    };

    const form = el(
      'form',
      {
        class: 'akc-ticket',
        novalidate: true,
        onsubmit: async (event) => {
          event.preventDefault();
          error.hidden = true;
          if (!EMAIL.test(email.value.trim())) return fail(S.ticket_invalid_email);
          if (needsPhone && phone.value.replace(/\D/g, '').length < 6) return fail(S.ticket_phone_required);
          if (summary.value.trim().length < 5) return fail(S.ticket_summary_required);
          if (!consent.checked) return fail(S.ticket_consent_required);

          submit.disabled = true;
          submit.textContent = S.ticket_sending;
          try {
            const post = () =>
              fetch(`${ENDPOINT}/api/tickets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sessionId: state.sessionId,
                  category: t.category,
                  summary: summary.value.trim(),
                  name: name.value.trim() || undefined,
                  email: email.value.trim(),
                  phone: phone.value.trim() || undefined,
                  orderNumber: order.value.trim() || undefined,
                  page: location.pathname,
                  consent: true,
                }),
              });
            let res = await post();
            let data = await res.json().catch(() => ({}));
            // Sesiune expirată sau ștearsă: serverul creează una nouă dacă nu trimitem id.
            if (res.status === 404 && data.error === 'session_not_found') {
              state.sessionId = undefined;
              res = await post();
              data = await res.json().catch(() => ({}));
            }
            if (res.ok && data.sessionId) state.sessionId = data.sessionId;
            if (res.status === 400 && data.field === 'email') throw new Error(S.ticket_invalid_email);
            if (res.status === 400 && data.field === 'phone') throw new Error(S.ticket_phone_required);
            if (!res.ok || !data.ticket) throw new Error(res.status === 429 ? S.error_rate : S.ticket_error);
            t.done = (S.ticket_done || '').replace('[email]', email.value.trim());
            persist();
            form.replaceWith(ticketBlock(msg));
          } catch (err) {
            fail(err.message || S.ticket_error);
            submit.disabled = false;
            submit.textContent = S.ticket_submit;
          }
        },
      },
      el('p', { class: 'akc-ticket__title', text: S.ticket_title }),
      field(S.ticket_name, name),
      field(S.ticket_email, email),
      field(needsPhone ? S.ticket_phone_contact : S.ticket_phone, phone),
      field(S.ticket_order, order),
      field(S.ticket_summary, summary),
      el(
        'div',
        { class: 'akc-consent' },
        consent,
        el(
          'label',
          { for: consent.id },
          `${S.ticket_consent} `,
          cfg.privacyUrl ? el('a', { href: cfg.privacyUrl, target: '_blank', rel: 'noopener', text: S.ticket_privacy }) : S.ticket_privacy,
          '.',
        ),
      ),
      error,
      submit,
    );
    return form;
  }

  function renderMessage(msg) {
    if (msg.role === 'user') {
      return el('div', { class: 'akc-msg akc-msg--user' }, el('div', { class: 'akc-text', text: msg.text }));
    }
    if (msg.role === 'error') {
      return el(
        'div',
        { class: 'akc-msg akc-msg--error' },
        el('div', { class: 'akc-text', text: msg.text }),
        msg.retry
          ? el('button', {
              type: 'button',
              class: 'akc-retry',
              text: S.retry,
              onclick: () => {
                const text = msg.retry;
                state.messages = state.messages.filter((m) => m !== msg);
                // Mesajul clientului rămâne afișat o singură dată.
                const lastUser = state.messages.at(-1);
                if (lastUser && lastUser.role === 'user' && lastUser.text === text) state.messages.pop();
                renderAll();
                send(text);
              },
            })
          : null,
      );
    }
    return el(
      'div',
      { class: 'akc-msg akc-msg--assistant' },
      msg.text ? el('div', { class: 'akc-text' }, markdown(msg.text)) : null,
      msg.products && msg.products.length ? el('div', { class: 'akc-cards' }, msg.products.map(productCard)) : null,
      msg.ticket ? ticketBlock(msg) : null,
    );
  }

  function renderAll() {
    if (!ui) return;
    ui.log.replaceChildren();
    if (state.messages.length === 0) {
      ui.log.append(renderMessage({ role: 'assistant', text: S.greeting }));
    }
    for (const msg of state.messages) ui.log.append(renderMessage(msg));
    scrollDown();
  }

  function scrollDown() {
    if (ui) ui.log.scrollTop = ui.log.scrollHeight;
  }

  function addMessage(msg) {
    state.messages.push(msg);
    ui.log.append(renderMessage(msg));
    scrollDown();
  }

  // ---------------------------------------------------------------- SSE

  async function* events(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });
      let sep;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        let event = 'message';
        let data = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data += line.slice(5).trimStart();
        }
        if (!data) continue;
        try {
          yield { event, data: JSON.parse(data) };
        } catch (_) {
          // eveniment corupt: îl sărim
        }
      }
      if (done) return;
    }
  }

  class HttpError extends Error {
    constructor(status) {
      super(`HTTP ${status}`);
      this.status = status;
    }
  }

  function errorMessage(err) {
    if (err instanceof HttpError) {
      if (err.status === 429) return { text: S.error_rate };
      if (err.status === 413) return { text: S.error_too_long };
    }
    return { text: S.error_generic, retry: true };
  }

  // ---------------------------------------------------------------- trimitere

  async function send(raw) {
    const text = String(raw || '').trim();
    if (!text || busy) return;
    if ([...text].length > MAX_CHARS) {
      state.messages.push({ role: 'error', text: S.error_too_long });
      renderAll();
      return;
    }

    busy = true;
    setBusy(true);
    state.messages.push({ role: 'user', text });
    persist();
    renderAll();
    const typing = showTyping();

    // Răspunsul se construiește din evenimente și se afișează pe măsură ce vin
    // (cardurile și formularul pot sosi înaintea textului).
    const reply = { role: 'assistant', text: '', products: [] };
    let replyNode = null;
    const show = () => {
      const node = renderMessage(reply);
      if (replyNode) replyNode.replaceWith(node);
      else if (typing.node.isConnected) ui.log.insertBefore(node, typing.node);
      else ui.log.append(node);
      replyNode = node;
      scrollDown();
    };

    try {
      const res = await fetch(`${ENDPOINT}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          sessionId: state.sessionId,
          page: { path: location.pathname, productHandle: cfg.productHandle || undefined },
        }),
      });
      if (!res.ok || !res.body) throw new HttpError(res.status);

      let failure = null;
      for await (const { event, data } of events(res.body)) {
        if (event === 'meta') {
          state.sessionId = data.sessionId;
        } else if (event === 'status') {
          typing.set(data.text);
        } else if (event === 'delta') {
          reply.text += data.text;
          typing.remove();
          show();
        } else if (event === 'products') {
          reply.products.push(...data.items);
          show();
        } else if (event === 'ticket_form') {
          reply.ticket = { category: data.category, summary: data.summary };
          show();
        } else if (event === 'error') {
          failure = data.message;
        }
      }

      typing.remove();
      if (failure) {
        if (replyNode) replyNode.remove();
        addMessage({ role: 'error', text: failure });
        // Dacă serverul a trimis și formularul, clientul ne poate lăsa totuși un mesaj.
        if (reply.ticket) addMessage({ role: 'assistant', text: '', products: [], ticket: reply.ticket });
      } else if (reply.text || reply.products.length || reply.ticket) {
        // Deja afișat prin show(); nu re-randăm, ca să nu pierdem ce scrie clientul în formular.
        state.messages.push(reply);
        if (!replyNode) show();
      } else {
        throw new Error('empty');
      }
    } catch (err) {
      typing.remove();
      if (replyNode) replyNode.remove();
      const e = errorMessage(err);
      addMessage({ role: 'error', text: e.text, retry: e.retry ? text : undefined });
    } finally {
      busy = false;
      setBusy(false);
      persist();
      if (!MOBILE.matches) ui.input.focus();
    }
  }

  /** Trei puncte animate; statusul tool-ului („Caut în catalog…”) apare ca text mic deasupra. */
  function showTyping() {
    const status = el('span', { class: 'akc-typing__status', hidden: true });
    const node = el(
      'div',
      { class: 'akc-msg akc-msg--assistant akc-typing', role: 'status' },
      status,
      el(
        'span',
        { class: 'akc-dots' },
        el('i', { 'aria-hidden': 'true' }),
        el('i', { 'aria-hidden': 'true' }),
        el('i', { 'aria-hidden': 'true' }),
        el('span', { class: 'akc-sr', text: S.typing }), // doar pentru cititoare de ecran
      ),
    );
    ui.log.append(node);
    scrollDown();
    return {
      node,
      set: (text) => {
        status.textContent = text;
        status.hidden = !text;
      },
      remove: () => node.remove(),
    };
  }

  function setBusy(value) {
    if (!ui) return;
    ui.send.disabled = value;
    ui.root.classList.toggle('akc-busy', value);
  }

  // ---------------------------------------------------------------- panou

  function build() {
    const input = el('textarea', {
      id: 'akc-input',
      class: 'akc-input__field',
      rows: 1,
      maxlength: MAX_CHARS,
      placeholder: S.placeholder,
      'aria-label': S.placeholder,
      enterkeyhint: 'send',
    });
    const sendBtn = el('button', { type: 'submit', class: 'akc-input__send', 'aria-label': S.send }, icon('send'));
    const log = el('div', { class: 'akc-log', role: 'log', 'aria-live': 'polite', 'aria-relevant': 'additions' });

    const submitInput = (event) => {
      if (event) event.preventDefault();
      const text = input.value;
      if (!text.trim() || busy) return;
      input.value = '';
      autosize();
      send(text);
    };

    const panel = el(
      'div',
      { class: 'akc-panel', id: 'akc-panel', role: 'dialog', 'aria-labelledby': 'akc-title', hidden: true },
      el(
        'div',
        { class: 'akc-head' },
        el(
          'div',
          { class: 'akc-head__text' },
          el('h2', { id: 'akc-title', class: 'akc-head__title', text: S.title }),
          el('p', { class: 'akc-head__sub', text: S.disclaimer }),
        ),
        el('button', { type: 'button', class: 'akc-icon-btn', title: S.new_chat, 'aria-label': S.new_chat, onclick: reset }, icon('reset')),
        el('button', { type: 'button', class: 'akc-icon-btn', 'aria-label': S.close, onclick: close }, icon('close')),
      ),
      log,
      el('form', { class: 'akc-input', onsubmit: submitInput }, input, sendBtn),
    );

    function autosize() {
      input.style.height = 'auto';
      input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
      input.style.overflowY = input.scrollHeight > 120 ? 'auto' : 'hidden';
    }
    input.addEventListener('input', autosize);
    input.addEventListener('keydown', (event) => {
      // Enter trimite (pe mobil tasta e etichetată „Trimite”, enterkeyhint); Shift+Enter = rând nou.
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) submitInput(event);
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    root.append(panel);
    ui = { root, panel, log, input, send: sendBtn };
    renderAll();
  }

  function open() {
    if (!ui) build();
    ui.panel.hidden = false;
    root.classList.add('akc-open');
    bubble.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.toggle('akc-lock', MOBILE.matches);
    state.open = true;
    persist();
    scrollDown();
    if (!MOBILE.matches) ui.input.focus();
  }

  function close() {
    if (!ui) return;
    ui.panel.hidden = true;
    root.classList.remove('akc-open');
    bubble.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('akc-lock');
    state.open = false;
    persist();
    bubble.focus();
  }

  /**
   * Deschide panoul pe formularul de tichet. Dacă ultimul mesaj e deja un formular netrimis,
   * îl înlocuiește (fără formulare duplicate la click-uri repetate).
   */
  let pendingTicket = null;
  function openTicket(opts) {
    const o = opts || {};
    if (!mounted) {
      pendingTicket = o;
      return;
    }
    const msg = {
      role: 'assistant',
      text: typeof o.intro === 'string' ? o.intro.slice(0, 500) : '',
      products: [],
      ticket: {
        category: typeof o.category === 'string' ? o.category : 'other',
        summary: typeof o.summary === 'string' ? o.summary.slice(0, 2000) : '',
      },
    };
    const last = state.messages.at(-1);
    if (last && last.ticket && !last.ticket.done) state.messages.pop();
    state.messages.push(msg);
    persist();
    open();
    renderAll();
    const forms = ui.log.querySelectorAll('.akc-ticket');
    const form = forms[forms.length - 1];
    if (form) {
      form.scrollIntoView({ block: 'start' });
      if (!MOBILE.matches) form.querySelector('input')?.focus();
    }
  }

  function reset() {
    if (busy) return;
    state.sessionId = undefined;
    state.messages = [];
    persist();
    renderAll();
    ui.input.focus();
  }

  let preconnected = false;
  function preconnect() {
    if (preconnected) return;
    preconnected = true;
    document.head.append(el('link', { rel: 'preconnect', href: new URL(ENDPOINT).origin }));
  }

  // ---------------------------------------------------------------- montare

  const root = el('div', { class: 'akc' });
  if (/^#[0-9a-f]{6}$/i.test(cfg.color || '')) root.style.setProperty('--akc-accent', cfg.color);

  const bubble = el(
    'button',
    {
      type: 'button',
      class: 'akc-bubble',
      'aria-label': S.open,
      'aria-expanded': 'false',
      'aria-controls': 'akc-panel',
      onclick: () => (ui && !ui.panel.hidden ? close() : open()),
      onpointerenter: preconnect,
      onfocus: preconnect,
    },
    icon('chat'),
  );
  root.append(bubble);

  let mounted = false;
  function mount() {
    mounted = true;
    document.body.append(root);
    document.documentElement.classList.add('akc-enabled');
    // Pe mobil, panoul full-screen nu se redeschide singur la navigare.
    if (state.open && !MOBILE.matches) open();
    else if (state.open) {
      state.open = false;
      persist();
    }
    if (pendingTicket) {
      const o = pendingTicket;
      pendingTicket = null;
      openTicket(o);
    }
  }

  // În afara drumului critic: CSS-ul se încarcă după `load`, bula apare când stilul e gata.
  function start() {
    // timeout: montează și dacă pagina nu ajunge „idle” (pagini grele, taburi în fundal).
    const idle = window.requestIdleCallback
      ? (fn) => window.requestIdleCallback(fn, { timeout: 3000 })
      : (fn) => setTimeout(fn, 200);
    idle(() => {
      if (!cfg.css) return mount();
      const link = el('link', { rel: 'stylesheet', href: cfg.css });
      link.addEventListener('load', mount, { once: true });
      link.addEventListener('error', () => {}, { once: true });
      document.head.append(link);
    });
  }

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });

  window.AkytaChat = { open: () => open(), close: () => close(), openTicket: (opts) => openTicket(opts) };
})();
